import { cmsNew } from "@/lib/cms/cms-new-i18n";

import type {
  ColumnsWidthProps,
  ColumnWidthWeight,
  GapInlineStyle,
  LayoutAlign,
  LayoutGap,
} from "./types";
import { columnTracksTemplate, cx } from "./internal";
import { resolveGapClasses, resolveGapLayout } from "./gap";
import { layoutAlignItemsClass } from "./size";
import { CMS_FLOW_CLASS } from "@/lib/cms-puck/block-style/defaults";

export function getColumnTrackSizeOptions(): ReadonlyArray<{
  label: string;
  value: ColumnWidthWeight;
}> {
  return [
    { label: cmsNew("cms.new.options.columnTrack.auto", "Contenu (auto)"), value: "auto" },
    { label: cmsNew("cms.new.options.columnTrack.1", "1 part (étroit)"), value: "1" },
    { label: cmsNew("cms.new.options.columnTrack.2", "2 parts"), value: "2" },
    { label: cmsNew("cms.new.options.columnTrack.3", "3 parts"), value: "3" },
    { label: cmsNew("cms.new.options.columnTrack.4", "4 parts"), value: "4" },
    { label: cmsNew("cms.new.options.columnTrack.5", "5 parts"), value: "5" },
    { label: cmsNew("cms.new.options.columnTrack.6", "6 parts"), value: "6" },
    { label: cmsNew("cms.new.options.columnTrack.8", "8 parts (large)"), value: "8" },
    { label: cmsNew("cms.new.options.columnTrack.10", "10 parts"), value: "10" },
    { label: cmsNew("cms.new.options.columnTrack.12", "12 parts (très large)"), value: "12" },
  ];
}


const ALIGN_SELF_CLASS: Record<LayoutAlign, string> = {
  start: "self-start",
  center: "self-center",
  end: "self-end",
  stretch: "self-stretch",
};

/** Colonnes (Columns) — classes grid complètes par nombre de colonnes. */
const COLUMNS_CLASS: Record<2 | 3 | 4, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

/** Empilement mobile (Columns) : 1 colonne <md, puis N colonnes. */
const COLUMNS_STACK_CLASS: Record<2 | 3 | 4, string> = {
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
  4: "grid-cols-1 md:grid-cols-4",
};

/** Nombre de colonnes normalisé (2|3|4) — tolère string/number (JSON, champs Puck). */
export function normalizeColumnsCount(n: unknown): 2 | 3 | 4 {
  const v = Number(n);
  if (v === 2) return 2;
  if (v === 4) return 4;
  return 3;
}

/** Classe Tailwind du bloc Columns (grid responsive, colonnes égales). */
export function columnsClass(props: {
  columns: 2 | 3 | 4;
  gap: LayoutGap;
  stackOnMobile: boolean;
  verticalAlign?: LayoutAlign;
}): string {
  const cols = normalizeColumnsCount(props.columns);
  return cx(
    "grid w-full",
    CMS_FLOW_CLASS,
    props.stackOnMobile ? COLUMNS_STACK_CLASS[cols] : COLUMNS_CLASS[cols],
    resolveGapClasses({ gap: props.gap }),
    layoutAlignItemsClass(props.verticalAlign ?? "stretch"),
  );
}

function parseColumnWeight(weight: ColumnWidthWeight | undefined): number {
  if (weight === "auto") return 1;
  const n = Number.parseInt(String(weight ?? "1"), 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(12, n);
}

/** Normalise une valeur de champ Puck (string/number) vers une largeur de piste. */
export function normalizeColumnWeight(
  weight: unknown,
): ColumnWidthWeight {
  if (weight === "auto" || String(weight ?? "").toLowerCase() === "auto") {
    return "auto";
  }
  const s = String(weight ?? "1");
  const allowed = new Set([
    "1", "2", "3", "4", "5", "6", "8", "10", "12",
  ]);
  return (allowed.has(s) ? s : "1") as ColumnWidthWeight;
}

/** Taille CSS d'une piste (`auto` ou `minmax(0, Nfr)`). */
export function columnTrackSize(weight: ColumnWidthWeight | undefined): string {
  if (normalizeColumnWeight(weight) === "auto") return "auto";
  return `minmax(0, ${parseColumnWeight(normalizeColumnWeight(weight))}fr)`;
}

/** Lit les largeurs actives depuis les props (clés `colNWidth`). */
export function readColumnsWidthProps(
  columns: 2 | 3 | 4,
  source: ColumnsWidthProps,
): ColumnsWidthProps {
  const n = normalizeColumnsCount(columns);
  const out: ColumnsWidthProps = {};
  columnSlotKeys(n).forEach((key) => {
    const widthKey = `${key}Width` as keyof ColumnsWidthProps;
    out[widthKey] = normalizeColumnWeight(source[widthKey]);
  });
  return out;
}

/** Tailles de pistes par slot actif (col1…colN). */
export function resolveColumnTrackSizes(
  columns: 2 | 3 | 4,
  widths: ColumnsWidthProps,
): string[] {
  const normalized = readColumnsWidthProps(columns, widths);
  return columnSlotKeys(normalizeColumnsCount(columns)).map((key) => {
    const widthKey = `${key}Width` as keyof ColumnsWidthProps;
    return columnTrackSize(normalized[widthKey]);
  });
}

/** `true` si au moins une piste est `auto` ou si les parts `fr` diffèrent. */
export function hasCustomColumnTracks(sizes: string[]): boolean {
  if (sizes.some((s) => s === "auto")) return true;
  if (sizes.length <= 1) return false;
  return sizes.some((s) => s !== sizes[0]);
}

/**
 * Layout du bloc Columns : `grid-template-columns` explicite (toujours inline).
 * Les classes Tailwind `grid-cols-*` ne gèrent pas les pistes `auto` / parts mixtes.
 */
export function columnsLayout(
  props: {
    columns: 2 | 3 | 4;
    gap: LayoutGap;
    gapCustomPx?: number | null;
    gapRow?: LayoutGap | null;
    gapCol?: LayoutGap | null;
    gapRowCustomPx?: number | null;
    gapColCustomPx?: number | null;
    stackOnMobile: boolean;
    verticalAlign?: LayoutAlign;
  } & ColumnsWidthProps,
): {
  className: string;
  gridTemplateColumns: string;
  gapStyle: GapInlineStyle;
} {
  const columns = normalizeColumnsCount(props.columns);
  const alignClass = layoutAlignItemsClass(props.verticalAlign ?? "stretch");
  const template = columnTracksTemplate(resolveColumnTrackSizes(columns, props));
  const gap = resolveGapLayout({
    gap: props.gap,
    gapCustomPx: props.gapCustomPx,
    gapRow: props.gapRow,
    gapCol: props.gapCol,
    gapRowCustomPx: props.gapRowCustomPx,
    gapColCustomPx: props.gapColCustomPx,
  });

  return {
    className: cx(
      "grid w-full",
    CMS_FLOW_CLASS,
      gap.className,
      alignClass,
      props.stackOnMobile ? COLUMNS_CUSTOM_STACK_CLASS : undefined,
    ),
    gridTemplateColumns: template,
    gapStyle: gap.style,
  };
}

/** Inversion de l'ordre des colonnes empilées sur mobile (via `order`). */
export function columnsReverseMobileCss(
  scopeClass: string,
  count: number,
): string {
  if (count < 2) return "";
  const rules = Array.from({ length: count }, (_, i) => {
    const nth = i + 1;
    return `.${scopeClass} > *:nth-child(${nth}){order:${count - i}}`;
  });
  return `@media (max-width:767px){${rules.join("")}}`;
}

/** Classe CSS pour largeurs custom + empilement mobile (cf. cms-canvas.css / globals.css). */
export const COLUMNS_CUSTOM_STACK_CLASS = "cms-columns-custom-stack";

/** Variable CSS posée en inline style sur la grille Columns. */
export const COLUMNS_TEMPLATE_VAR = "--cms-cols-template";

/** Classes du conteneur direct d'une cellule Columns (alignement + largeur auto). */
export function columnCellClassName(
  trackSize: string,
  verticalAlign: LayoutAlign = "stretch",
): string {
  const widthClass = trackSize === "auto" ? "w-auto max-w-full" : "w-full";
  return cx("relative min-w-0", widthClass, ALIGN_SELF_CLASS[verticalAlign]);
}

/** Index 1-based des slots de colonnes effectivement utilisés (2|3|4). */
export function columnSlotKeys(
  columns: 2 | 3 | 4,
): ("col1" | "col2" | "col3" | "col4")[] {
  const all = ["col1", "col2", "col3", "col4"] as const;
  return all.slice(0, normalizeColumnsCount(columns)) as (
    | "col1"
    | "col2"
    | "col3"
    | "col4"
  )[];
}
